import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import T, { Translate } from '../i18n/i18n';
import ResourceImage from '../ResourceImage/ResourceImage';
import FormChar from './FormChar';
import Checkbox from '../Forms/Checkbox/Checkbox';
import math from '../../services/expression';
import { Container, VerticalSpace, Section, SectionCollapse, CollapseState, ActionButtonWrapper, ExtraActions, ActionButton, Row, CheckboxWrapper, ConfirmMessage, TokenIndicator } from './Characters.styles';
import { CharListItem, CharGroup, CharListAvatar, CharListInfo, CharListName, CharListPlayer, CharValue, CharValueInput, CharListDetail, FilterField, NoChars } from './CharList.styles';
import { listChars, createChar, updateChar, removeMultiChar } from '../../services/chars';
import { rtcBufferActions, charactersActions } from '../../redux/actions';

export default ({tableId}) => {

    const dispatch = useDispatch();
    const [CharsList, setCharsList] = useState([]);
    const [renderCharsList, setRenderCharsList] = useState([]);
    const [formQuickValues, setFormQuickValues] = useState({});
    const [isOpenedForm, setIsOpenedForm] = useState(false);
    const [formId, setFormId] = useState(null);
    const [filterValue, setFilterValue] = useState('');
    const [showPlayerList, setShowPlayerList] = useState(true);
    const [showNPCsList, setShowNPCsList] = useState(true);
    const [listviewMode, setListviewMode] = useState(JSON.parse(localStorage.getItem('config.charViewMode') ?? '0'));
    const [multiSelect, setMultiSelect] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);
    const [confirmMultiDelete, setConfirmMultiDelete] = useState(false);

    const charactersStored = useSelector(state => state.charactersState.characters);
    const playerId = useSelector(state => state.authState.id);
    const isOwner = useSelector(state => state.authState.isOwner);
    const selectedChar = useSelector(state => state.charactersState.selectedCharId);
    const tokensOnGrid = useSelector(state => state.gridtoolsState.tokensOnGrid);

    useEffect(() => {
        let quickValues = {};
        for (let char of CharsList) {
            quickValues[char.id] = {
                'red': char.dataset.redCurrent,
                'green': char.dataset.greenCurrent,
                'blue': char.dataset.blueCurrent
            };
        }
        setFormQuickValues(quickValues);
    }, [CharsList]);

    useEffect(() => {
        setCharsList(charactersStored);
    }, [charactersStored]);

    useEffect(() => {
        setFormId(selectedChar);
        setIsOpenedForm(Boolean(selectedChar));
    }, [selectedChar]);

    useEffect(() => {
        if (filterValue.length > 0) {
            let tags = filterValue.split(' ');
            setRenderCharsList(CharsList.filter(i => tags.every(tag => i.tags.includes(tag))));
        } else {
            setRenderCharsList(CharsList);
        }
    }, [CharsList, filterValue]);

    useEffect(()=>{
        if(!selectedItems || selectedItems.length === 0){
            setConfirmMultiDelete(false);
        }
    }, [selectedItems]);

    useEffect(() => {
        (async () => {
            try {
                let chars = await listChars(tableId);
                if (chars.length) {
                    dispatch(charactersActions.setCharacters(chars || []));
                    setCharsList(chars || []);
                }
            } catch(e) {
                console.log(e);
            }
        })();
    }, [dispatch, tableId]);


    const handleAddNewChar = async () => {
        let char = {
            name: '',
            avatar: '',
            playerId: '',
            redMax: '',
            redCurrent: '',
            redVisibility: '',
            greenMax: '',
            greenCurrent: '',
            greenVisibility: '',
            blueMax: '',
            blueCurrent: '',
            blueVisibility: ''
        };

        let chars = await createChar(tableId, char);
        
        dispatch(rtcBufferActions.pushOutputBuffer({
            type: 'charSync',
            data: [...chars.chars]
        }));
        dispatch(charactersActions.setCharacters(chars.chars));
        setCharsList([...chars.chars]);
        setFormId(chars.id);
        setIsOpenedForm(true);
    }

    const handleOpenChar = async (c, e) => {
        dispatch(charactersActions.selectChar(c.id));

        if (e.shiftKey) {
            dispatch(charactersActions.seekForToken(c.id));
        }
    }

    const selectValue = (e) => {
        e.target.select();
    }

    const setCharValue = (id, key, value) => {
        if (getQuickValues(id, key) === value) {
            return;
        }

        const newQuickValues = {...formQuickValues};
        newQuickValues[id][key] = value;
        setFormQuickValues(newQuickValues);
    }

    const getQuickValues = (id, key) => {
        if (!formQuickValues[id] || !formQuickValues[id][key]) {
            return '';
        }
        return formQuickValues[id][key];
    }

    const saveQuickValues = async (char) => {
        const newCharList = [...CharsList];
        let charSend = null;
        let shouldUpdate = false;
        for (let i in newCharList) {
            if (!newCharList[i] || newCharList[i].id !== char.id) {
                continue;
            }

            let values = formQuickValues[newCharList[i].id];

            if (newCharList[i].dataset.redCurrent === values.red && 
                newCharList[i].dataset.greenCurrent === values.green &&
                newCharList[i].dataset.blueCurrent === values.blue) {

                shouldUpdate = false;
                continue;
            }

            if (/^[+-]/gi.test(String(values.red))) {
                let valAsFloat = String(newCharList[i].dataset.redCurrent).replace(/[^()\d.+-]/gi, '') || '0';
                let result = math.evaluate(valAsFloat + '' + values.red);
                newCharList[i].dataset.redCurrent = result >= 0 ? result : `(${result})`;
            } else {
                newCharList[i].dataset.redCurrent = values.red || '';
            }

            if (/^[+-]/gi.test(String(values.green))) {
                let valAsFloat = String(newCharList[i].dataset.greenCurrent).replace(/[^()\d.+-]/gi, '') || '0';
                let result = math.evaluate(valAsFloat + '' + values.green);
                newCharList[i].dataset.greenCurrent = result >= 0 ? result : `(${result})`;
            } else {
                newCharList[i].dataset.greenCurrent = values.green || '';
            }

            if (/^[+-]/gi.test(String(values.blue))) {
                let valAsFloat = String(newCharList[i].dataset.blueCurrent).replace(/[^()\d.+-]/gi, '') || '0';
                let result = math.evaluate(valAsFloat + '' + values.blue);
                newCharList[i].dataset.blueCurrent = result >= 0 ? result : `(${result})`;
            } else {
                newCharList[i].dataset.blueCurrent = values.blue || '';
            }

            charSend = newCharList[i];
            shouldUpdate = true;
        }

        if (!shouldUpdate) {
            return;
        }

        let charResp = await updateChar(tableId, {
            id: charSend.id,
            name: charSend.name,
            avatar: charSend.avatar,
            playerId: charSend.player,
            playerName: charSend.playerName,
            redMax: charSend.dataset.redMax,
            redCurrent: charSend.dataset.redCurrent,
            redVisibility: charSend.dataset.redVisibility,
            blueMax: charSend.dataset.blueMax,
            blueCurrent: charSend.dataset.blueCurrent,
            blueVisibility: charSend.dataset.blueVisibility,
            greenMax: charSend.dataset.greenMax,
            greenCurrent: charSend.dataset.greenCurrent,
            greenVisibility: charSend.dataset.greenVisibility,
        });

        setCharsList((chars) => chars.map((i) => i.id !== charSend.id ? i : charResp));
        dispatch(charactersActions.updateChar(charSend.id, charResp));
        dispatch(rtcBufferActions.pushOutputBuffer({
            type: 'charUpdateSync',
            data: {id: charSend.id, char: charResp}
        }));
    }

    const updateFilterValue = (value) => {
        setFilterValue(value);
    }

    const closeForm = () => {
        dispatch(charactersActions.selectChar(null));
        setIsOpenedForm(false)
    }

    const handleKeyPress = async (e, char) => {
        if (String(e.key).toUpperCase() !== 'ENTER') {
            return;
        }

        saveQuickValues(char);

        e.preventDefault();
        e.stopPropagation();

    }

    const keyCancel = (e) => {
        e.stopPropagation();
    }

    const toggleMultiSelect = () => {
        if (multiSelect) {
            setSelectedItems([]);
        }
        setMultiSelect(v => !v);
    }

    const toggleSelectedItem = (e, id) => {
        setSelectedItems(s => s.indexOf(id)>=0 ? s.filter(i => i !== id) : [...s, id]);
        e.stopPropagation();
    }


    const handleChangeVisibility = (mode) => {
        setListviewMode(mode);
        localStorage.setItem('config.charViewMode',JSON.stringify(mode))
    }

    const performDeleteMany = async () => {
        await removeMultiChar(tableId, selectedItems);

        dispatch(rtcBufferActions.pushOutputBuffer({
            type: 'charMultiRemoveSync',
            data: {ids: selectedItems}
        }));
        dispatch(charactersActions.removeMultiChar(selectedItems, true));
        setCharsList(chars => [...chars].filter(i => !selectedItems.includes(i.id)));

        setSelectedItems([]);
        setConfirmMultiDelete(false);
    };

    const startDraggingChar = (char, event) => {
        event.dataTransfer.setData("text/plain", char.id);
        event.dataTransfer.effectAllowed = "link";
        event.dataTransfer.dropEffect = "link";
    };

    const renderChar = (char, index) => {
        return <CharListItem 
            draggable="true" 
            className={'draggable ' + (listviewMode === 0 ? 'expanded' : 'compact')} 
            onClick={(e)=>{handleOpenChar(char, e)}} 
            onDragStart={(e)=>{startDraggingChar(char, e)}} 
            key={index} >
        <CharListAvatar className={listviewMode === 0 ? '' : 'compact'}>
            {listviewMode === 0 ? <ResourceImage resourceId={char.avatar} /> : null }
            {multiSelect ? <CheckboxWrapper><Checkbox className='solo' onClick={(v,e) => toggleSelectedItem(e, char.id)} value={selectedItems.includes(char.id)} /></CheckboxWrapper> : null}
        </CharListAvatar>
        {tokensOnGrid.public.some(t => t.props && t.props.token === char.id) || (isOwner && tokensOnGrid.private.some(t => t.props && t.props.token === char.id)) 
            ? <TokenIndicator title={Translate('gridtools.tokenOnGrid') }/> : null }
        <CharListInfo>
            <CharListName>{char.name}</CharListName>
            {listviewMode === 0 && char.tags ? <CharListDetail>{char.tags}</CharListDetail> : null }
            {listviewMode === 0 && char.playerName ? <CharListPlayer>{char.playerName}</CharListPlayer> : null }
        </CharListInfo>
        {listviewMode === 0 ? <>
            <CharValue onFocus={(e)=>selectValue(e)} onClick={(e) => {e.stopPropagation()}}><CharValueInput color={'#f33'} onChange={(e) => {setCharValue(char.id , 'red', e.target.value)}} onBlur={() => {saveQuickValues(char)}} onKeyPress={(e)=>handleKeyPress(e, char)} value={getQuickValues(char.id,'red')} /></CharValue>
            <CharValue onFocus={(e)=>selectValue(e)} onClick={(e) => {e.stopPropagation()}}><CharValueInput color={'#0c0'} onChange={(e) => {setCharValue(char.id , 'green', e.target.value)}} onBlur={() => {saveQuickValues(char)}} onKeyPress={(e)=>handleKeyPress(e, char)} value={getQuickValues(char.id,'green')} /></CharValue>
            <CharValue onFocus={(e)=>selectValue(e)} onClick={(e) => {e.stopPropagation()}}><CharValueInput color={'#03f'} onChange={(e) => {setCharValue(char.id , 'blue', e.target.value)}} onBlur={() => {saveQuickValues(char)}} onKeyPress={(e)=>handleKeyPress(e, char)} value={getQuickValues(char.id,'blue')} /></CharValue>
        </> : null }
    </CharListItem>
    }

    return <Container onKeyDown={keyCancel}>
        {!isOpenedForm ? <>
            <Row>
                <Section style={{flex:4}}><T value="characters.filtertag"/></Section>
                <Section style={{flex:1}}><T value="characters.viewmode"/></Section>
            </Row>
            <Row>
                <FilterField onChange={(e) => {updateFilterValue( e.target.value)}} value={filterValue} />
                <Row>
                    <ActionButton color="#da0" width="30px" className={listviewMode === 0 ? 'active' : ''} onClick={()=>handleChangeVisibility(0)}><ExtraActions color="#da0" icon="\F0572" title={Translate('characters.viewlarge')} /></ActionButton>
                    <ActionButton color="#da0" width="30px" className={listviewMode === 1 ? 'active' : ''} onClick={()=>handleChangeVisibility(1)}><ExtraActions color="#da0" icon="\F0571" title={Translate('characters.viewcompact')} /></ActionButton>
                </Row>
            </Row>
            {!renderCharsList || renderCharsList.length < 1 ? 
                <NoChars>
                    <T value="characters.noChars" />
                </NoChars> 
            : (listviewMode === 0 ? <>
                    {isOwner && <SectionCollapse onClick={() => setShowPlayerList(!showPlayerList)}><CollapseState className={!showPlayerList ? 'collapsed' : ''}/><T value="characters.playable"/></SectionCollapse>}
                    {showPlayerList && <CharGroup>
                        {renderCharsList.filter(i => isOwner ? !!i.player : i.player === playerId).map((char, k) => renderChar(char, k))}
                    </CharGroup>}
                    {isOwner && <CharGroup>
                        <SectionCollapse onClick={() => setShowNPCsList(!showNPCsList)}><CollapseState className={!showNPCsList ? 'collapsed' : ''}/><T value="characters.npc"/></SectionCollapse>
                            {showNPCsList && renderCharsList.filter(i => !i.player).map((char, k) => renderChar(char, k))}
                    </CharGroup>}
                    <VerticalSpace />
                </> : <>
                    {renderCharsList.filter(i => isOwner ? true : i.player === playerId).map((char, k) => renderChar(char, k))}
                    <VerticalSpace />
                </>)}
            {confirmMultiDelete ? <ActionButtonWrapper>
                <ConfirmMessage><T value={`characters.confirmMessage1${selectedItems.length === 1 ? 's' : 'p'}`}/> {selectedItems.length} <T value={`characters.confirmMessage2${selectedItems.length === 1 ? 's' : 'p'}`} /></ConfirmMessage>
                <ActionButton color="#f44" onClick={()=>performDeleteMany()}><T value="yes"/></ActionButton>
                <ActionButton color="#0c0" onClick={()=>setConfirmMultiDelete(false)}><T value="no"/></ActionButton>
            </ActionButtonWrapper> : <ActionButtonWrapper>
                {selectedItems.length > 0 && multiSelect ?
                    <ActionButton color="#f44" onClick={()=>{setConfirmMultiDelete(true)}}><T value="characters.deleteMany"/></ActionButton>
                :
                    <ActionButton color="#0C0" onClick={handleAddNewChar}><T value="characters.newChar"/></ActionButton>
                }
                { isOwner ?
                    <ActionButton color="#47F" width="30px" onClick={() => {toggleMultiSelect(true)}} title={Translate('characters.ablemultiselect')}><ExtraActions icon={"\\F0139"}/></ActionButton>
                : null }
            </ActionButtonWrapper>}
        </> : <FormChar
            charId={formId}
            charsList={CharsList}
            onClose={() => {closeForm()}}
            setCharsList={setCharsList}
            tableId={tableId}
         />}
    </Container>;
};